import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryRetailService from '../../../services/nurseryRetail';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';
import { errorMessage } from '../../../utils/apiResponse';
import type { XdockMoveResult, XdockStaleOrder } from '../../../types/nurseryRetail';

/**
 * Open orders a schedule change has left on a day their store is no longer
 * picked. A public holiday sheet usually arrives after that week's purchase
 * orders are in. Nothing moves by itself: each order is ticked and moved.
 *
 * `refreshKey` changes whenever the schedules do, so the list is read again.
 */
export function XdockRecheckPanel({ refreshKey }: { refreshKey: number }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'XdockRecheckPanel' });
  const tz = useOrgTimezone();
  const [stale, setStale] = useState<XdockStaleOrder[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<XdockMoveResult[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await nurseryRetailService.recheck();
      if (res.success) {
        setStale(res.data || []);
        setPicked(new Set());
      }
    } catch {
      // The schedules themselves are still usable; this list is an extra.
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const movable = stale.filter((s) => s.movable);
  const toggle = (id: number) => setPicked((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const move = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryRetailService.moveOrders([...picked]);
      if (res.success) setResults(res.data || []);
      else setError(errorMessage(res, t('moveFailed')));
      await load();
    } catch (e) {
      setError(errorMessage(e, t('moveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const refused = results.filter((r) => !r.moved);
  const movedCount = results.filter((r) => r.moved).length;

  if (!stale.length && !results.length) return null;

  return (
    <section aria-label={t('ariaLabel')} className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
      {stale.length > 0 && (
        <>
          <div className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
            <p className="min-w-0">
              <span className="font-semibold">
                {t('staleHeadline', { count: stale.length })}
              </span>{' '}
              {t('staleHint')}
            </p>
          </div>

          <ul className="space-y-2">
            {stale.map((s) => (
              <li key={s.order_id} className="bg-white border border-amber-200 rounded-lg p-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 flex-shrink-0"
                    checked={picked.has(s.order_id)}
                    disabled={!s.movable}
                    onChange={() => toggle(s.order_id)}
                    aria-label={t('moveOne', { order: s.order_number })}
                  />
                  <span className="min-w-0 text-sm">
                    <span className="block font-medium text-neutral-900 break-words">
                      <Link to={`/nursery/orders/${s.order_id}`} className="text-primary-700 hover:underline">{s.order_number}</Link>
                      {' · '}{t('customerStore', { name: s.customer_name, store: s.store_code })}
                    </span>
                    <span className="block text-neutral-600 break-words">
                      {s.customer_po ? t('po', { po: s.customer_po }) : ''}{t('trees', { count: s.total_trees })}
                      {s.load_number ? t('onLoad', { load: s.load_number }) : ''}
                    </span>
                    <span className="block text-neutral-800 break-words">
                      {s.current_date ? formatLocalDateInTz(s.current_date, tz) : t('noDay')}
                      {s.new_date
                        ? ` → ${formatLocalDateInTz(s.new_date, tz)}${s.new_route ? ` (${s.new_route})` : ''}`
                        : ''}
                    </span>
                    {s.reason && <span className="block text-red-700 break-words">{s.reason}</span>}
                    {!s.movable && !s.reason && s.load_number && (
                      <span className="block text-red-700 break-words">
                        {t('beingPicked', { load: s.load_number })}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          {movable.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={move} disabled={busy || picked.size === 0}>
                {busy ? t('moving') : picked.size ? t('moveCount', { count: picked.size }) : t('move')}
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => setPicked(new Set(picked.size === movable.length ? [] : movable.map((m) => m.order_id)))}>
                {picked.size === movable.length ? t('clear') : t('selectAll')}
              </Button>
            </div>
          )}
        </>
      )}

      {error && <p className="text-sm text-red-700">{error}</p>}
      {movedCount > 0 && (
        <p className="text-sm text-green-800">
          {t('moved', { count: movedCount })}
        </p>
      )}
      {refused.map((r) => (
        <p key={r.order_id} className="text-sm text-red-700 break-words">{r.order_number}: {r.message}</p>
      ))}
    </section>
  );
}
